import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { useGetCampaignsCountV2Query, useGetCampaignsV2Query } from '@/services/endpoints/campaigns';
import { CampaignV2ProviderType, CampaignV2Response } from '@/services/endpoints/campaigns/types';
import { CalendarCampaign } from './communicationsCalendarData';

const toCalendarCampaign = (campaign: CampaignV2Response): CalendarCampaign => ({
  id: campaign.id,
  name: campaign.name,
  providerType: campaign.providerType,
  status: campaign.status,
  scheduledStart: campaign.scheduledStart,
  createdOn: campaign.createdOn,
  updatedOn: campaign.updatedOn,
  stats: campaign.stats,
});

const useProviderCampaigns = (providerType: CampaignV2ProviderType) => {
  const workspaceId = useWorkspace().id;
  const countQuery = useGetCampaignsCountV2Query({ workspaceId, providerType });
  const count = countQuery.data ?? 0;
  const listQuery = useGetCampaignsV2Query(
    { workspaceId, providerType, offset: 0, limit: count },
    { skip: countQuery.isLoading || countQuery.isUninitialized || countQuery.isError || count === 0 }
  );
  const isLoading =
    !countQuery.isError &&
    !listQuery.isError &&
    (countQuery.isLoading || countQuery.isUninitialized || (count > 0 && (listQuery.isLoading || listQuery.isUninitialized)));

  const refetch = () => {
    void countQuery.refetch();
    if (!listQuery.isUninitialized) void listQuery.refetch();
  };

  return {
    campaigns: (listQuery.data?.items ?? []).filter((campaign) => campaign.status !== 'DELETED').map(toCalendarCampaign),
    isLoading,
    isError: countQuery.isError || listQuery.isError,
    refetch,
  };
};

export const useCalendarCampaigns = () => {
  const whatsapp = useProviderCampaigns('WHATSAPP');
  const sms = useProviderCampaigns('SMS');
  const email = useProviderCampaigns('EMAIL');

  const providers = [whatsapp, sms, email];

  return {
    campaigns: providers.flatMap((provider) => provider.campaigns),
    isLoading: providers.some((provider) => provider.isLoading),
    isError: providers.some((provider) => provider.isError),
    refetch: () => providers.forEach((provider) => provider.refetch()),
  };
};
